import React from 'react';
import SEO from '../components/SEO';
import NeuralBackground from '../components/NeuralBackground';
import { MessageSquare, Phone, Zap, Brain, Users, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const concepts = [
  {
    icon: Brain,
    title: 'What is an AI Agent?',
    description: 'Think of it as your perfect employee who never gets tired, never calls in sick, and works exactly how you want them to.',
    details: [
      'Understands what people are asking',
      'Makes smart decisions based on your rules',
      'Works 24 hours a day, 7 days a week',
      'Gets better at their job over time'
    ],
    example: 'Like having a receptionist who can answer questions, book appointments, and update your calendar - but they never take a break, never have a bad day, and work for free.',
    painPoint: 'Tired of being the only one who can handle customer questions?'
  },
  {
    icon: MessageSquare,
    title: 'WhatsApp AI Agents',
    description: 'Your customers already use WhatsApp every day. Now they can get instant help from your business, even at midnight.',
    details: [
      'Responds to customers instantly, any time',
      'Shows menus, prices, and availability',
      'Takes orders and bookings automatically',
      'Sends confirmations and updates'
    ],
    example: 'A pizza shop owner goes home at 6pm. His WhatsApp bot keeps taking orders all evening, answers questions about ingredients, and tells customers exactly when their pizza will be ready for pickup.',
    painPoint: 'Missing sales because you can\'t respond to messages fast enough?'
  },
  {
    icon: Phone,
    title: 'Voice AI Agents',
    description: 'AI that sounds like a real person and can make and receive phone calls, handling requests professionally while you focus on important work.',
    details: [
      'Sounds completely natural and human',
      'Understands different accents and speech',
      'Books appointments in your calendar',
      'Transfers important calls to you when needed'
    ],
    example: 'A busy dentist\'s AI calls patients every Monday morning to confirm their appointments for the week. If someone needs to reschedule, it finds a new time that works and updates the calendar automatically.',
    painPoint: 'Constantly interrupted by phone calls when you\'re trying to work?'
  },
  {
    icon: Zap,
    title: 'Business Automation',
    description: 'Stop copying information between different apps. We make all your business tools talk to each other automatically.',
    details: [
      'Connects all your different business apps',
      'Moves data between systems instantly',
      'Triggers actions when things happen',
      'Sends you alerts only when you need them'
    ],
    example: 'When someone fills out your website contact form, it automatically creates a customer record in your CRM, sends them a welcome email, adds them to your newsletter, and sends you a notification with their details.',
    painPoint: 'Spending hours copying information between different systems?'
  }
];

const useCases = [
  {
    business: 'Restaurant Owner',
    problem: 'Sarah was exhausted from taking phone orders during dinner rush while trying to manage the kitchen and serve customers',
    solution: 'WhatsApp bot takes orders, shows the menu, handles special dietary requests, and sends pickup notifications',
    benefit: 'Sarah now enjoys dinner with her family while her AI handles evening orders. Revenue increased 30% from never missing a customer.',
    emotion: 'From stressed and overwhelmed to peaceful family dinners'
  },
  {
    business: 'Medical Practice',
    problem: 'Dr. Martinez was losing money from no-shows and his receptionist was overwhelmed with appointment calls all day',
    solution: 'Voice AI calls patients to confirm appointments, reschedules when needed, and sends reminder texts',
    benefit: 'No-shows dropped by 80%. Receptionist can focus on patient care instead of being stuck on the phone.',
    emotion: 'From chaos and lost revenue to smooth operations and happy patients'
  },
  {
    business: 'Online Store',
    problem: 'Mark spent 3 hours every morning processing orders, updating inventory, and sending customer emails instead of growing his business',
    solution: 'Automation connects website, inventory, shipping, and email systems to work together seamlessly',
    benefit: 'Orders process instantly, customers get real-time updates, inventory updates automatically. Mark got his mornings back.',
    emotion: 'From working weekends to having time for his hobbies again'
  },
  {
    business: 'Service Business',
    problem: 'Lisa was losing potential customers because she couldn\'t respond to inquiries fast enough while serving existing clients',
    solution: 'AI agent qualifies leads, books consultations, and follows up with potential customers automatically',
    benefit: 'Never misses a lead, even outside business hours. Conversion rate increased 45% from instant responses.',
    emotion: 'From anxiety about missed opportunities to confidence in growth'
  }
];

export default function AIExplainers() {
  return (
    <>
      <SEO 
        title="AI & Automation Explained Simply"
        description="Understand AI agents, WhatsApp bots, voice assistants, and business automation in simple terms. No tech jargon, just clear explanations of how AI can give you your life back."
        keywords={[
          'AI Agents Explained',
          'WhatsApp Bots',
          'Voice AI',
          'Business Automation',
          'AI for Business',
          'Chatbots',
          'n8n',
          'Make Automation',
          'Simple AI Explanation',
          'Business AI Guide'
        ]}
      />
      <div className="relative min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 pt-32 pb-20">
        <div className="absolute inset-0">
          <NeuralBackground />
        </div>
        <div className="relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Hero Section */}
            <div className="text-center mb-20">
              <h1 className="text-5xl md:text-7xl font-bold mb-6">
                <span className="text-gradient">Get Your Life Back with AI</span>
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mt-6 mb-16">
                No tech jargon here. Just clear, simple explanations of how AI agents, 
                WhatsApp bots, and automation can stop you from working nights and weekends.
              </p>
            </div>

            {/* Main Concepts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
              {concepts.map((concept, index) => (
                <div
                  key={index}
                  className="glass-card p-8 rounded-xl backdrop-blur-md transform hover:scale-105 transition-all duration-300"
                >
                  <div className="flex items-center mb-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-500/20 to-purple-500/20 p-2.5 flex items-center justify-center">
                      <concept.icon className="h-6 w-6 text-teal-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white ml-4">{concept.title}</h3>
                  </div>

                  <div className="mb-5">
                    <div className="inline-block bg-red-900/20 text-red-400 px-3 py-1.5 rounded-md border border-red-700/30 mb-3 shadow-sm">
                      <p className="text-sm font-medium">{concept.painPoint}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-300 mb-8">{concept.description}</p>
                  
                  <div className="space-y-3 mb-8">
                    {concept.details.map((detail, i) => (
                      <div key={i} className="flex items-center text-sm text-teal-400">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mr-2" />
                        {detail}
                      </div>
                    ))}
                  </div>

                  <div className="p-4 bg-teal-500/10 rounded-lg">
                    <p className="text-sm text-gray-300">
                      <strong className="text-teal-400">Real Example:</strong> {concept.example}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* How We Build These Solutions */}
            <div className="glass-card p-10 rounded-xl backdrop-blur-md mb-20">
              <h2 className="text-3xl font-bold text-white mb-6 text-center">How We Build Your Freedom</h2>
              <div className="grid md:grid-cols-2 gap-10 mt-8">
                <div>
                  <h3 className="text-xl font-bold text-teal-400 mb-4">n8n - Your Digital Assistant</h3>
                  <p className="text-gray-300 mb-6">
                    Think of n8n as your super-organized digital assistant that connects all your business apps 
                    so they work together perfectly, without you having to do anything.
                  </p>
                  <ul className="space-y-3 text-gray-300">
                    <li>• Connects over 400 different business apps</li>
                    <li>• No technical knowledge required</li>
                    <li>• Works 24/7 in the background</li>
                    <li>• Handles complex tasks automatically</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-teal-400 mb-4">Make - Your Smart Coordinator</h3>
                  <p className="text-gray-300 mb-6">
                    Make is like having a brilliant coordinator who can juggle multiple tasks across 
                    different apps at the same time, making sure everything happens in perfect order.
                  </p>
                  <ul className="space-y-3 text-gray-300">
                    <li>• Connects over 2000 apps and services</li>
                    <li>• Handles complex decision-making</li>
                    <li>• Real-time processing and updates</li>
                    <li>• Visual workflow that makes sense</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Real Business Examples */}
            <div className="mb-20">
              <h2 className="text-3xl font-bold text-center mb-12">
                <span className="text-gradient">Real People Getting Their Lives Back</span>
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {useCases.map((useCase, index) => (
                  <div key={index} className="glass-card p-6 rounded-xl backdrop-blur-md">
                    <h3 className="text-xl font-bold text-white mb-5">{useCase.business}</h3>
                    
                    <div className="space-y-5">
                      <div>
                        <div className="inline-block bg-red-900/20 text-red-400 px-3 py-1.5 rounded-md border border-red-700/30 mb-3 shadow-sm">
                          <p className="text-sm font-semibold">The Struggle:</p>
                        </div>
                        <p className="text-gray-300">{useCase.problem}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-semibold text-blue-400 mb-1">Our Solution:</p>
                        <p className="text-gray-300">{useCase.solution}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm font-semibold text-green-400 mb-1">The Result:</p>
                        <p className="text-gray-300">{useCase.benefit}</p>
                      </div>

                      <div className="pt-3 border-t border-gray-700 mt-2">
                        <p className="text-sm font-semibold text-purple-400 mb-1">Life Transformation:</p>
                        <p className="text-gray-300 italic">{useCase.emotion}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-center mt-10">
              <div className="glass-card p-10 rounded-xl backdrop-blur-md max-w-3xl mx-auto">
                <h2 className="text-2xl font-bold text-gradient mb-4">
                  Ready to Stop Working Nights and Weekends?
                </h2>
                <p className="text-gray-300 mt-4 mb-10">
                  Take our quick assessment to see how ready your business is for automation that actually works, 
                  or let's have a conversation about getting your life back. No technical knowledge required!
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Link
                    to="/tools/ai-readiness-assessment"
                    className="inline-flex items-center px-8 py-3 text-lg font-medium rounded-md text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 transition-all duration-200"
                  >
                    <Brain className="h-5 w-5 mr-2" />
                    <span>See If You're Ready for Freedom</span>
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center px-8 py-3 text-lg font-medium rounded-md text-white hover:text-white border border-gray-700 hover:border-teal-500/50 transition-all duration-200"
                  >
                    <Users className="h-5 w-5 mr-2" />
                    <span>Get Your Life Back</span>
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}